import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CampaignService from '../../../services/CampaignService';
import Globals from '../../../core/globals';
import { OfflineCache } from '../../services/OfflineCache';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

function CampaignDetailedAndroid() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [loadingCsv, setLoadingCsv] = useState(false);
    const [loadingTop, setLoadingTop] = useState(false);
    const [loadingLottery, setLoadingLottery] = useState(false);
    const [loadingCheck, setLoadingCheck] = useState(false);
    const [topItems, setTopItems] = useState(null);
    const [containsText, setContainsText] = useState('');
    const [topItemsError, setTopItemsError] = useState('');
    const [lotteryError, setLotteryError] = useState('');
    const [csvError, setCsvError] = useState('');
    const [checkContainsError, setCheckContainsError] = useState('');

    useEffect(() => {
        const fetchCampaign = async () => {
            try {
                const response = await CampaignService.getCampaignById(id);
                setCampaign(response.data);
                OfflineCache.saveCampaignDetail(id, response.data);
            } catch (e) {
                const cached = OfflineCache.getCampaignDetail(id);
                if (cached) {
                    setCampaign(cached);
                } else {
                    setError('Error loading campaign');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchCampaign();
    }, [id]);

    const exportCsv = async () => {
        setLoadingCsv(true);
        try {
            const token = localStorage.getItem('authToken');
            const resp = await fetch(`${Globals.backendUri}/campaigns/csv/${id}`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) throw new Error(`Failed to export CSV: ${resp.status}`);
            const blob = await resp.blob();
            // Convert blob to base64 (without data: prefix)
            const base64 = await new Promise((resolve, reject) => {
                try {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const res = reader.result || '';
                        const b64 = String(res).split(',')[1] || '';
                        resolve(b64);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                } catch (err) {
                    reject(err);
                }
            });
            const fileName = `${(campaign?.name || 'campaign').replace(/[^a-z0-9-_]+/gi, '_')}.csv`;
            await Filesystem.writeFile({
                path: fileName,
                data: base64,
                directory: Directory.Documents,
            });
            const fileUri = await Filesystem.getUri({ path: fileName, directory: Directory.Documents });
            // Try to open share sheet with the file
            try {
                await Share.share({ title: 'Export CSV', url: fileUri.uri });
            } catch (_) {}
            console.log('CSV exported to', fileUri.uri);
        } catch (e) {
            console.error('Export CSV error', e);
            setError('Failed to export CSV');
        } finally {
            setLoadingCsv(false);
        }
    };

    const getTopItems = async () => {
        setLoadingTop(true);
        setTopItemsError('');
        try {
            const response = await CampaignService.getTopItems(id);
            setTopItems(response.data);
        } catch (error) {
            setTopItemsError("Error loading top items: " + error.response?.data);
        } finally {
            setLoadingTop(false);
        }
    };

    const performLottery = async () => {
        setLoadingLottery(true);
        setLotteryError('');
        try {
            const response = await CampaignService.getLotteryWinner(id);
            setCampaign((prev) => ({ ...prev, lotteryWinner: response.data }));
        } catch (error) {
            setLotteryError("Error performing lottery: " + error.response?.data);
        } finally {
            setLoadingLottery(false);
        }
    };

    const checkContains = async () => {
        setLoadingCheck(true);
        setCheckContainsError('');
        try {
            const response = await CampaignService.checkContains(id);
            setContainsText(
                `Out of ${response.data.numberOfPreSaves} pre-saves on Spotify, ${response.data.stillContaining} still have the song in their library. This is ${Math.round((response.data.stillContaining / response.data.numberOfPreSaves) * 100)}%.`
            );
        } catch (error) {
            setCheckContainsError("Error checking contains: " + error.response?.data);
        } finally {
            setLoadingCheck(false);
        }
    };

    const jumpToLandingPage = () => {
        window.open(`https://labelboost.net/${campaign.urlSuffix}`, '_blank');
    };

    const getArtistNamesFormatted = () => {
        const artists = [...(campaign.featureArtistsNames || [])];
        if (artists.length === 0) {
            return campaign.artistName;
        } else if (artists.length === 1) {
            return `${campaign.artistName} and ${artists[0]}`;
        } else {
            const lastArtist = artists.pop();
            return `${campaign.artistName}, ${artists.join(', ')}, and ${lastArtist}`;
        }
    };

    return (
        <div className="px-4 py-4">
            {loading ? (
                <div className="text-center py-10">Loading...</div>
            ) : !campaign ? (
                <div className="text-center py-10">Not found</div>
            ) : (
                <div>
                    {error ? <div className="text-red-600 mb-3">{error}</div> : null}
                    {campaign.artworkUrl ? (
                        <img src={campaign.artworkUrl} alt={campaign.name} className="w-full h-48 object-cover rounded mb-3" />
                    ) : null}
                    <h2 className="text-2xl font-bold mb-1">{campaign.name}</h2>
                    <p className="text-gray-700 mb-1">Artist: {getArtistNamesFormatted()}</p>
                    <p className="text-gray-700 mb-3">Release: {new Date(campaign.releaseDate).toLocaleDateString('de-DE')}</p>

                    {/* Pre-save Statistics */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <h3 className="text-lg font-semibold mb-2">Pre-save Statistics</h3>
                        <div className="space-y-1 text-sm">
                            <p className="text-gray-700">Spotify Pre-saves: <span className="font-semibold">{campaign.numberOfSaves}</span></p>
                            <p className="text-gray-700">Apple Music Pre-saves: <span className="font-semibold">{campaign.numberOfSavesApple}</span></p>
                            {campaign.isReleased ? (
                                <>
                                    <p className="text-gray-700">Campaign clicks (post-release): <span className="font-semibold">{campaign.numberOfCampaignClicksPostRelease}</span></p>
                                    <p className="text-gray-700">Spotify clicks: <span className="font-semibold">{campaign.numberOfSpotifyClicks}</span></p>
                                    <p className="text-gray-700">Apple Music clicks: <span className="font-semibold">{campaign.numberOfAppleMusicClicks}</span></p>
                                    <p className="text-gray-700">Deezer clicks: <span className="font-semibold">{campaign.numberOfDeezerClicks}</span></p>
                                    <p className="text-gray-700">Click-through rate: <span className="font-semibold">{Math.round(
                                        ((campaign.numberOfSpotifyClicks + campaign.numberOfDeezerClicks + campaign.numberOfAppleMusicClicks) / campaign.numberOfCampaignClicksPostRelease) * 100
                                    )}%</span></p>
                                </>
                            ) : (
                                <p className="text-gray-700">Pre-save page clicks: <span className="font-semibold">{campaign.numberOfCampaignClicksPreRelease}</span></p>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        <button className={`bg-blue-600 text-white rounded-md px-3 py-2 text-sm ${loadingTop || topItems ? 'opacity-50' : ''}`} disabled={loadingTop || topItems} onClick={getTopItems}>
                            {loadingTop ? 'Loading…' : 'Analytics'}
                        </button>
                        <button className={`bg-blue-600 text-white rounded-md px-3 py-2 text-sm ${loadingLottery || campaign.lotteryWinner ? 'opacity-50' : ''}`} disabled={loadingLottery || campaign.lotteryWinner} onClick={performLottery}>
                            {loadingLottery ? 'Loading…' : 'Lottery'}
                        </button>
                        <button className={`bg-blue-600 text-white rounded-md px-3 py-2 text-sm ${loadingCsv ? 'opacity-50' : ''}`} disabled={loadingCsv} onClick={exportCsv}>
                            {loadingCsv ? 'Exporting…' : 'Export CSV'}
                        </button>
                        <button className={`bg-blue-600 text-white rounded-md px-3 py-2 text-sm`} onClick={jumpToLandingPage}>
                            Landing Page
                        </button>
                        {campaign.isReleased && (
                            <button className={`bg-blue-600 text-white rounded-md px-3 py-2 text-sm ${loadingCheck ? 'opacity-50' : ''}`} disabled={loadingCheck} onClick={checkContains}>
                                {loadingCheck ? 'Checking…' : 'Check Contains'}
                            </button>
                        )}
                    </div>

                    {/* Error Messages */}
                    {topItemsError && <div className="text-red-600 text-sm mb-2">{topItemsError}</div>}
                    {lotteryError && <div className="text-red-600 text-sm mb-2">{lotteryError}</div>}
                    {csvError && <div className="text-red-600 text-sm mb-2">{csvError}</div>}
                    {checkContainsError && <div className="text-red-600 text-sm mb-2">{checkContainsError}</div>}

                    {/* Lottery Winner */}
                    {campaign.lotteryWinner && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                            <p className="text-green-800 font-semibold">🎉 Lottery Winner: {campaign.lotteryWinner}</p>
                        </div>
                    )}

                    {/* Contains Text */}
                    {containsText && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                            <p className="text-blue-800 text-sm">{containsText}</p>
                        </div>
                    )}

                    {/* Analytics Results */}
                    {topItems && (
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <h3 className="text-lg font-semibold mb-3">📊 User Analytics</h3>
                            
                            <div className="mb-4">
                                <h4 className="font-medium text-gray-800 mb-2">Top Artists</h4>
                                <ul className="list-disc list-inside text-sm text-gray-700 ml-2">
                                    {topItems.artists && topItems.artists.length > 0 ? (
                                        topItems.artists.map((artist, index) => (
                                            <li key={index}>{artist}</li>
                                        ))
                                    ) : (
                                        <li className="text-gray-500">No top artists available</li>
                                    )}
                                </ul>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-medium text-gray-800 mb-2">Top Genres</h4>
                                <ul className="list-disc list-inside text-sm text-gray-700 ml-2">
                                    {topItems.genre && topItems.genre.length > 0 ? (
                                        topItems.genre.map((genre, index) => (
                                            <li key={index}>{genre}</li>
                                        ))
                                    ) : (
                                        <li className="text-gray-500">No top genres available</li>
                                    )}
                                </ul>
                            </div>

                            <div>
                                <h4 className="font-medium text-gray-800 mb-2">Top Songs</h4>
                                <ul className="list-disc list-inside text-sm text-gray-700 ml-2">
                                    {topItems.songs && topItems.songs.length > 0 ? (
                                        topItems.songs.map((song, index) => (
                                            <li key={index}>{song}</li>
                                        ))
                                    ) : (
                                        <li className="text-gray-500">No top songs available</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CampaignDetailedAndroid;


